import { AlertTriangle, BellRing, CheckCircle2, Clock3, RefreshCw } from "lucide-react";
import { MaterialPreparationPanel } from "../MaterialPreparationPanel";
import { groupWarehousePreparationTaskBatches, preparationNotificationAction } from "../warehouse-preparation-view";
import { fmt, duration, warehouseGroupTitle } from "./warehouse-presentation";
import { WarehouseTaskCard as TaskCard } from "./WarehouseTaskCard";
import type { WarehousePreparationProps, useWarehousePreparationWorkspace } from "./useWarehousePreparationWorkspace";
export function WarehousePreparationView({user, notify, dataRefreshVersion, workspace}: WarehousePreparationProps & {
 workspace: ReturnType<typeof useWarehousePreparationWorkspace>;
}) {
 const { workerRole, loading, error, metrics, desktopState, roleCopy,
 activeTaskCount, urgentTaskCount, completedTaskCount, openNotifications,
 orderedGroups, load, setTasks, testDesktopNotifications,
 enableDesktopNotifications, disableDesktopNotifications, openNotification } = workspace;
  return (
    <main className="warehouse-preparation-module">
      <header className="warehouse-task-heading">
        <div>
          <h1>{roleCopy.title}</h1>
          <p>{roleCopy.description}</p>
        </div>
        <button type="button" onClick={() => void load()} disabled={loading}>
          <RefreshCw size={16} />
          {loading ? "Yükleniyor" : "Yenile"}
        </button>
      </header>
      {user.role === "SISTEM_YONETICISI" ? (
        <section
          className={`warehouse-desktop-notifications is-${desktopState.toLowerCase()}`}
          aria-label="Sistem yöneticisi masaüstü bildirimleri"
        >
          <BellRing size={18} />
          <div>
            <strong>
              {desktopState === "ON"
                ? "Masaüstü bildirimleri açık"
                : desktopState === "BLOCKED"
                  ? "Bildirim izni engellenmiş"
                  : desktopState === "UNSUPPORTED"
                    ? "Bu tarayıcı masaüstü bildirimini desteklemiyor"
                    : "Masaüstü bildirimleri kapalı"}
            </strong>
            <span>
              {desktopState === "ON"
                ? "Yeni depo aksiyonları yaklaşık 30 saniye içinde bu bilgisayara bildirilir."
                : desktopState === "BLOCKED"
                  ? "Tarayıcı ayarlarından localhost bildirim iznini açın."
                  : "Yalnız bu bilgisayar ve sistem yöneticisi hesabı için etkinleşir."}
            </span>
          </div>
          {desktopState === "ON" ? (
            <div>
              <button
                type="button"
                onClick={() => void testDesktopNotifications()}
              >
                Sesli test gönder
              </button>
              <button type="button" onClick={disableDesktopNotifications}>
                Kapat
              </button>
            </div>
          ) : desktopState === "OFF" ? (
            <button
              type="button"
              onClick={() => void enableDesktopNotifications()}
            >
              Aç ve test et
            </button>
          ) : null}
        </section>
      ) : null}
      {workerRole ? (
        <section className="warehouse-role-guide" aria-label="Görev akışı">
          <div>
            <strong>Bugünkü akış</strong>
            <span>Görevleri yukarıdan aşağıya tamamlayın.</span>
          </div>
          <ol>
            {roleCopy.steps.map((step, index) => (
              <li key={step}>
                <span>{index + 1}</span>
                {step}
              </li>
            ))}
          </ol>
        </section>
      ) : null}
      {workerRole ? (
        <section className="warehouse-task-summary" aria-label="Görev özeti">
          <span><strong>{activeTaskCount}</strong> bekleyen</span>
          <span className={urgentTaskCount ? "is-critical" : undefined}>
            <strong>{urgentTaskCount}</strong> öncelikli
          </span>
          <span><strong>{completedTaskCount}</strong> tamamlanan</span>
        </section>
      ) : metrics ? (
        <section
          className="warehouse-task-metrics"
          aria-label="Son 30 gün hazırlık göstergeleri"
        >
          <div>
            <span>Açık görev</span>
            <strong>{metrics.openTaskCount}</strong>
          </div>
          <div
            className={metrics.criticalTaskCount ? "is-critical" : undefined}
          >
            <span>Kritik</span>
            <strong>{metrics.criticalTaskCount}</strong>
          </div>
          <div>
            <span>Üretime hazır</span>
            <strong>{metrics.readyTaskCount}</strong>
          </div>
          <div>
            <span>İstisna kapalı</span>
            <strong>{metrics.exceptionClosedTaskCount}</strong>
          </div>
          <div>
            <span>Ort. sahiplenme</span>
            <strong>{duration(metrics.averageClaimMinutes)}</strong>
          </div>
          <div>
            <span>Ort. tamamlama</span>
            <strong>{duration(metrics.averageCompletionMinutes)}</strong>
          </div>
        </section>
      ) : null}
      {openNotifications.length ? (
        <section
          className="warehouse-notifications"
          aria-label="Açık malzeme hazırlık bildirimleri"
        >
          <header>
            <strong>Aksiyon bildirimleri</strong>
            <span>{openNotifications.length} açık</span>
          </header>
          {openNotifications.slice(0, 5).map((item) => (
            <button
              type="button"
              key={item.id}
              className={`is-${item.severity.toLowerCase()}`}
              onClick={() => void openNotification(item)}
            >
              <AlertTriangle size={17} />
              <span>
                <strong>{item.title}</strong>
                <small>{item.message}</small>
              </span>
              <em>{preparationNotificationAction(item).label}</em>
            </button>
          ))}
        </section>
      ) : null}
      {error ? (
        <div className="warehouse-task-state is-error" role="alert">
          <AlertTriangle size={20} />
          <strong>{error}</strong>
        </div>
      ) : loading ? (
        <div className="warehouse-task-state" role="status">
          <Clock3 size={20} />
          Görevler hazırlanıyor…
        </div>
      ) : orderedGroups.every((group) => group.items.length === 0) ? (
        <div className="warehouse-task-state">
          <CheckCircle2 size={22} />
          <div>
            <strong>{roleCopy.emptyTitle}</strong>
            <span>{roleCopy.emptyDescription}</span>
          </div>
        </div>
      ) : (
        orderedGroups.map((group) =>
          group.items.length ? (
            <section
              className={`warehouse-task-group is-${group.key}`}
              key={group.key}
            >
              <h2>
                {warehouseGroupTitle(group.key, user.role, group.title)}
                <span>{group.items.length}</span>
              </h2>
              <div>
                {groupWarehousePreparationTaskBatches(group.items).map(
                  (batch) => (
                    <section className="warehouse-task-batch" key={batch.key}>
                      {batch.tasks.length > 1 ? (
                        <header>
                          <strong>
                            Toplu dalga · {batch.tasks[0].materialCode} ·{" "}
                            {fmt(batch.totalQuantity, batch.tasks[0].unit)}
                          </strong>
                          <span>
                            {batch.tasks.length} üretim işi: {batch.workOrders}
                          </span>
                        </header>
                      ) : null}
                      {batch.tasks.map((task) => (
                        <TaskCard
                          key={task.id}
                          task={task}
                          user={user}
                          notify={notify}
                          onRefresh={load}
                          onSaved={(next) =>
                            setTasks((current) =>
                              current.map((item) =>
                                item.id === next.id ? next : item,
                              ),
                            )
                          }
                        />
                      ))}
                    </section>
                  ),
                )}
              </div>
            </section>
          ) : null,
        )
      )}
      {!workerRole ? (
        <MaterialPreparationPanel
          user={user}
          notify={notify}
          refreshVersion={dataRefreshVersion}
        />
      ) : null}
    </main>
  );
}
